import React, { useMemo } from "react";
import { Alert, Box, Typography } from "@mui/material";
import FieldRenderer from "./fields/FieldRenderer";
import { LEAVE_KIND_FULL_DAY, LEAVE_KIND_HOURS } from "./fields/codeLabels";

/**
 * 양식 정의(fields) → 입력 화면.
 *
 * 유형별로 손으로 짠 렌더러 대신 정의를 읽어 그린다. 관리자가 양식에 칸을 하나 추가하면
 * 프론트 배포 없이 그 칸이 나타난다.
 */
export default function DynamicForm({ form, values, onChange, context }) {
    // useMemo 로 감싼다 — 매 렌더마다 새 배열이 되면 아래 useMemo 의존성이 계속 바뀐다.
    const fields = useMemo(() => form?.fields || [], [form]);

    /** 값 하나 변경 — 파생값(차감 시간)과 조건부 칸 정리를 여기서 함께 처리한다. */
    const setValue = (key, v) => {
        const next = applyDerived(fields, { ...values, [key]: v });
        // 조건이 깨져 사라진 칸의 값은 지운다. 안 지우면 «기타»로 적어 둔 상세가
        // 용도를 «교통비»로 바꾼 뒤에도 따라가서, 화면에 없는 값이 상신된다.
        fields.forEach((f) => {
            if (f.showIf && !isVisible(f, next) && next[f.key] != null) next[f.key] = null;
        });
        onChange(next);
    };

    const slotOf = (slot) => fields.find((f) => f.slot === slot);
    const hasLeave = !!slotOf("LEAVE_KIND");

    const summary = useMemo(() => (hasLeave ? leaveSummary(fields, values) : null), [hasLeave, fields, values]);

    if (fields.length === 0) {
        return (
            <Alert severity="warning" sx={{ fontSize: 12.5 }}>
                이 양식에는 입력 칸이 정의돼 있지 않습니다. 결재 양식 관리에서 칸을 추가해 주세요.
            </Alert>
        );
    }

    return (
        <Box>
            {form.notice && (
                <Alert severity="info" icon={false}
                       sx={{
                           mb: 2, fontSize: 12, lineHeight: 1.7, whiteSpace: "pre-line",
                           bgcolor: "#fffbeb", color: "#78350f", border: "1px solid #fde68a",
                       }}>
                    {form.notice}
                </Alert>
            )}

            {/* 차감 시간은 계산 결과라 눈에 띄게 따로 보여준다 — 숨기면 며칠 깎이는지 모른 채 올리게 된다. */}
            {summary && (
                <Box sx={{
                    mb: 2, px: 1.5, py: 1, borderRadius: 1,
                    bgcolor: "#eff6ff", border: "1px solid #bfdbfe",
                }}>
                    <Typography sx={{ fontSize: 12.5, color: "#1e40af" }}>{summary}</Typography>
                </Box>
            )}

            {fields.filter((f) => isVisible(f, values)).map((f) => (
                <FieldRenderer
                    key={f.key}
                    field={f}
                    value={values[f.key]}
                    onChange={(v) => setValue(f.key, v)}
                    context={context}
                />
            ))}
        </Box>
    );
}

/**
 * 조건부 표시 — {@code showIf} 가 없으면 항상 보인다.
 *
 * <pre>
 *   "showIf": { "key": "purpose", "equals": "기타" }   // 다른 칸이 특정 값일 때만
 *   "showIf": { "key": "purpose", "in": ["기타","교통비"] }
 * </pre>
 *
 * 서버도 같은 규칙으로 판단한다({@code ApprovalSlotMapper}). 한쪽만 알면 화면엔 없는 칸을
 * 서버가 «필수인데 비었다»고 막는 상태가 되므로 규칙을 한 벌로 맞춘다.
 */
export function isVisible(field, values) {
    const c = field?.showIf;
    if (!c || !c.key) return true;
    const v = values?.[c.key];
    if (Array.isArray(c.in)) return c.in.includes(v);
    return v === c.equals;
}

/**
 * 파생 슬롯 재계산 — 지금은 {@code LEAVE_HOURS} 하나.
 *
 * 서버가 최종 판단하지만 화면에서도 같은 규칙으로 보여준다. 값이 서로 다르면 사용자가
 * "화면엔 8시간이라더니 4시간 깎였다"를 겪게 되므로 규칙을 한 벌로 맞춘다.
 */
function applyDerived(fields, values) {
    const kindField = fields.find((f) => f.slot === "LEAVE_KIND");
    const hoursField = fields.find((f) => f.slot === "LEAVE_HOURS");
    if (!kindField || !hoursField) return values;

    const hours = computeLeaveHours(fields, values);
    return hours == null ? values : { ...values, [hoursField.key]: hours };
}

function computeLeaveHours(fields, values) {
    const kindField = fields.find((f) => f.slot === "LEAVE_KIND");
    const startField = fields.find((f) => f.slot === "PERIOD_START");
    const endField = fields.find((f) => f.slot === "PERIOD_END");
    if (!kindField) return null;

    // 대체휴무 사용은 종류가 HIDDEN 고정이라 값이 안 들어 있을 수 있다.
    const kind = values[kindField.key] || (kindField.type === "HIDDEN" ? "COMP_DAY" : null);
    if (!kind) return null;

    const perDay = LEAVE_KIND_HOURS[kind];
    if (perDay == null) return null;
    if (!LEAVE_KIND_FULL_DAY.includes(kind)) return perDay;   // 반차 계열은 기간을 곱하지 않는다

    const s = values[startField?.key];
    const e = values[endField?.key] || s;
    if (!s || !e) return null;
    const days = Math.floor((new Date(e) - new Date(s)) / 86400000) + 1;
    return days > 0 ? days * perDay : null;
}

function leaveSummary(fields, values) {
    const hours = computeLeaveHours(fields, values);
    if (hours == null) return null;
    const days = hours / 8;
    const dayText = Number.isInteger(days) ? `${days}일` : `${days}일`;
    return `이 신청으로 ${hours}시간(${dayText})이 차감됩니다.`;
}
